import { useCallback, useEffect, useState } from "react";
import { api, errMsg } from "../lib/api.js";
import { logError } from "../lib/logger.js";
import { useLookup } from "../context/LookupContext.js";
import { Card } from "./ui/card.js";
import { SelectField } from "./ui/select-field.js";
import { Badge, type BadgeProps } from "./ui/badge.js";
import { Pagination, type PaginationMeta } from "./ui/pagination.js";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "./ui/table.js";

interface HistoryRow {
  id: string;
  resourceType: string;
  resourceId: string;
  action: string;
  field: string | null;
  oldValue: string | null;
  newValue: string | null;
  performedAt: string;
  metadata: Record<string, unknown> | null;
  performedBy: { email: string; employee: { firstName: string; lastName: string } | null } | null;
}

const AREAS: Record<string, string> = {
  LEAVE_TYPE_CONFIG: "Leave Types",
  LEAVE_POLICY:      "Allocation Rules",
  LEAVE_BLACKOUT:    "Blackout Periods",
  LEAVE_DEPT_WFH:    "WFH & Departments",
  LEAVE_HOLIDAY:     "National Holidays",
  LEAVE_COMP_OFF:    "Comp-off",
  LEAVE_BALANCE:     "Employee Balances",
  LEAVE_RUN:         "Seed / Rollover",
};

const ACTIONS: Record<string, { label: string; tone: BadgeProps["tone"] }> = {
  CREATE:         { label: "Created",     tone: "success" },
  CREATE_VERSION: { label: "New version", tone: "info" },
  UPDATE:         { label: "Updated",     tone: "warning" },
  DELETE:         { label: "Deleted",     tone: "error" },
  CREDIT:         { label: "Credited",    tone: "accent" },
  RUN:            { label: "Run",         tone: "neutral" },
};

function parse(v: string | null): unknown {
  if (v == null) return null;
  try { return JSON.parse(v) as unknown; } catch { return v; }
}

function show(v: unknown): string {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (typeof v === "string" && /^\d{4}-\d{2}-\d{2}T/.test(v)) return v.slice(0, 10);
  if (typeof v === "object") return Object.entries(v as Record<string, unknown>)
    .filter(([, x]) => x !== null && x !== undefined)
    .map(([k, x]) => `${k}: ${show(x)}`).join(", ");
  return String(v);
}

// Which item the row is about, in the words the admin used on the other tabs.
function itemLabel(r: HistoryRow, leaveTypeName: (code: string) => string): string {
  const m = r.metadata ?? {};
  const str = (k: string) => (typeof m[k] === "string" ? (m[k] as string) : undefined);
  switch (r.resourceType) {
    case "LEAVE_TYPE_CONFIG": return leaveTypeName(r.resourceId);
    case "LEAVE_POLICY": {
      const lt = str("leaveType");
      const scope = [str("country"), str("employeeType")].filter(Boolean).join(" · ");
      const from = str("effectiveFrom");
      return [lt ? leaveTypeName(lt) : "Policy", scope, from ? `from ${from}` : ""].filter(Boolean).join(" — ");
    }
    case "LEAVE_BALANCE": {
      const lt = str("leaveType");
      return [str("employeeName"), lt ? leaveTypeName(lt) : "", m["year"] ? String(m["year"]) : ""].filter(Boolean).join(" · ");
    }
    case "LEAVE_COMP_OFF": return [str("employeeName"), str("holiday")].filter(Boolean).join(" · ");
    case "LEAVE_RUN":      return str("run") ?? r.resourceId;
    default:               return str("name") ?? r.resourceId;
  }
}

function changeText(r: HistoryRow): string {
  if (r.action === "UPDATE") return `${r.field ?? "value"}: ${show(parse(r.oldValue))} → ${show(parse(r.newValue))}`;
  if (r.action === "DELETE") return show(parse(r.oldValue));
  if (r.action === "CREATE_VERSION") {
    const m = r.metadata ?? {};
    const v = parse(r.newValue) as Record<string, unknown> | null;
    return show({
      days: v?.["daysAllowed"], tiers: m["tiers"], carryForward: v?.["carryForward"],
      noticeDays: v?.["minNoticeDays"], note: m["changeNote"],
    });
  }
  return show(parse(r.newValue));
}

export function LeaveChangeHistory() {
  const leaveTypes = useLookup("LEAVE_TYPE");
  const leaveTypeName = useCallback(
    (code: string) => leaveTypes.find(t => t.value === code)?.displayName ?? code,
    [leaveTypes],
  );
  const [area, setArea] = useState("");
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState<HistoryRow[]>([]);
  const [meta, setMeta] = useState<PaginationMeta>({ total: 0, page: 1, limit: 50, totalPages: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    setLoading(true);
    const qs = new URLSearchParams({ page: String(page), limit: "50", ...(area ? { resourceType: area } : {}) });
    api.get<{ data: HistoryRow[]; meta: PaginationMeta }>(`/admin/leave/history?${qs.toString()}`)
      .then(r => { setRows(r.data.data); setMeta(r.data.meta); setError(""); })
      .catch(err => { logError("LeaveChangeHistory.load", err); setError(errMsg(err) ?? "Could not load the change history"); })
      .finally(() => setLoading(false));
  }, [area, page]);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <p className="max-w-2xl text-[13px] text-surface-500">
          Every change made in Leave Admin, newest first. Policy changes are saved as new versions, so earlier versions stay intact here and under Allocation Rules.
        </p>
        <SelectField size="sm" className="w-[200px]" value={area} onChange={e => { setArea(e.target.value); setPage(1); }}>
          <option value="">All areas</option>
          {Object.entries(AREAS).map(([k, label]) => <option key={k} value={k}>{label}</option>)}
        </SelectField>
      </div>

      <Card className="overflow-x-auto p-0">
        <Table className="min-w-[860px]">
          <TableHeader>
            <TableRow>
              <TableHead className="w-[150px]">When</TableHead>
              <TableHead className="w-[160px]">Changed by</TableHead>
              <TableHead className="w-[150px]">Area</TableHead>
              <TableHead>Item</TableHead>
              <TableHead className="w-[110px]">Action</TableHead>
              <TableHead>Change</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (
              <TableRow><TableCell colSpan={6} className="py-8 text-center text-surface-400">Loading history…</TableCell></TableRow>
            )}
            {!loading && error && (
              <TableRow><TableCell colSpan={6} className="py-8 text-center text-[var(--color-error-fg)]">{error}</TableCell></TableRow>
            )}
            {!loading && !error && rows.length === 0 && (
              <TableRow><TableCell colSpan={6} className="py-8 text-center text-surface-400">No changes recorded yet. Changes appear here as soon as an admin saves one.</TableCell></TableRow>
            )}
            {!loading && !error && rows.map(r => {
              const action = ACTIONS[r.action] ?? { label: r.action, tone: "neutral" as const };
              const who = r.performedBy?.employee
                ? `${r.performedBy.employee.firstName} ${r.performedBy.employee.lastName}`
                : r.performedBy?.email ?? "—";
              return (
                <TableRow key={r.id}>
                  <TableCell className="whitespace-nowrap text-[12px] tabular-nums text-surface-500">
                    {new Date(r.performedAt).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                  </TableCell>
                  <TableCell className="text-[13px]">{who}</TableCell>
                  <TableCell className="text-[13px] text-surface-600">{AREAS[r.resourceType] ?? r.resourceType}</TableCell>
                  <TableCell className="text-[13px] font-medium">{itemLabel(r, leaveTypeName)}</TableCell>
                  <TableCell><Badge tone={action.tone}>{action.label}</Badge></TableCell>
                  <TableCell className="break-words text-[12px] text-surface-600">{changeText(r)}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Card>

      {meta.totalPages > 1 && <Pagination meta={meta} onPageChange={setPage} />}
    </div>
  );
}
